<template>
  <div>
    <div>
      <div class="editor">
        <div class="bubble-menu">
          <v-btn-toggle
            v-model="toggle_multiple"
            density="compact"
            multiple
            class="toolbar-compact"
          >
            <v-btn
              :class="{ 'is-active': editor.isActive('bold') }"
              size="x-small"
              density="compact"
              class="px-1 min-w-0"
              @click="editor.chain().focus().toggleBold().run()"
            >
              <v-icon>mdi-format-bold</v-icon>
            </v-btn>

            <v-btn
              :class="{ 'is-active': editor.isActive('italic') }"
              size="x-small"
              density="compact"
              class="px-1 min-w-0"
              @click="editor.chain().focus().toggleItalic().run()"
            >
              <v-icon>mdi-format-italic</v-icon>
            </v-btn>

            <v-btn
              :class="{ 'is-active': editor.isActive('underline') }"
              size="x-small"
              density="compact"
              class="px-1 min-w-0"
              @click="editor.chain().focus().toggleUnderline().run()"
            >
              <v-icon>mdi-format-underline</v-icon>
            </v-btn>

            <v-btn
              :class="{ 'is-active': editor.isActive('blockquote') }"
              size="x-small"
              density="compact"
              class="px-1 min-w-0"
              @click="insertBlockquote"
            >
              <v-icon>mdi-code-brackets</v-icon>
            </v-btn>

            <!-- Separator -->
            <div class="toolbar-separator"></div>

            <!-- Mention buttons -->
            <v-btn
              title="Mention user (@)"
              size="x-small"
              density="compact"
              class="px-1 min-w-0"
              @click="insertUserMention"
            >
              <v-icon>mdi-account</v-icon>
            </v-btn>

            <v-btn
              title="Mention contact (#)"
              size="x-small"
              density="compact"
              class="px-1 min-w-0"
              @click="insertContactMention"
            >
              <v-icon>mdi-office-building</v-icon>
            </v-btn>
          </v-btn-toggle>

          <v-spacer />

          <!-- Dynamic quick action buttons -->
          <v-btn-toggle
            v-if="quickActions && quickActions.length"
            density="compact"
            class="toolbar-compact"
          >
            <v-btn
              v-for="(action, idx) in quickActions"
              :key="idx"
              size="x-small"
              density="compact"
              class="px-2 min-w-0"
              @click="applyTemplate(action.template)"
            >
              <v-icon start size="12">mdi-lightning-bolt</v-icon>
              {{ action.name }}
            </v-btn>
          </v-btn-toggle>
        </div>
        <div class="editor-wrapper">
          <editor-content class="editor__content" :editor="editor" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useContactsStoreRefactor } from "@/stores/contactsStoreRefactor";
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import MentionList from "./MentionList.vue";
import { VueRenderer } from "@tiptap/vue-3";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import {
  docToPlaceholderText,
  findPhraseRange,
} from "@/utils/mentionPlaceholders";

export default {
  name: "AtMentionBox",
  components: {
    EditorContent,
  },
  props: {
    modelValue: String,
    instance_id: Number,
    // Deprecated legacy prop, kept for compatibility
    quick_links: {
      type: String,
      default: "",
      required: false,
    },
    // New dynamic quick actions: [{ name: string, template: string }]
    quickActions: {
      type: Array,
      default: () => [],
      required: false,
    },
  },
  setup(props, { emit }) {
    const usersStore = useUsersStoreRefactor();
    const contactsStore = useContactsStoreRefactor();
    const toggle_multiple = ref([]);
    let tippyInstance = null;

    // Clean up on unmount
    onBeforeUnmount(() => {
      if (tippyInstance) {
        tippyInstance.destroy();
      }
    });

    // Fetch users and contacts on mount
    onMounted(async () => {
      if (!usersStore.users.length) {
        await usersStore.fetchAll();
      }
      if (!contactsStore.contacts.length) {
        await contactsStore.fetchAll();
      }
    });

    const users = computed(() => {
      const activeUsers = usersStore.activeUsersInCurrentTenant || [];
      return Array.isArray(activeUsers)
        ? [...activeUsers, { id: "*", name: "Iedereen" }]
        : [{ id: "*", name: "Iedereen" }];
    });

    const contacts = computed(() => {
      return contactsStore.contacts || [];
    });

    // Helper to create mention suggestion config
    const createMentionSuggestion = (char, items) => ({
      char,
      items: ({ query }) => {
        return items.value
          .filter((item) =>
            item.name.toLowerCase().includes(query.toLowerCase()),
          )
          .slice(0, 10);
      },
      render: () => {
        let component;
        return {
          onStart: (props) => {
            if (tippyInstance) {
              tippyInstance.destroy();
            }
            component = new VueRenderer(MentionList, { props, editor });
            const virtualElement = document.createElement("div");
            document.body.appendChild(virtualElement);
            const tippyInstances = tippy(virtualElement, {
              getReferenceClientRect: props.clientRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: "manual",
              placement: "bottom-start",
            });
            tippyInstance = Array.isArray(tippyInstances)
              ? tippyInstances[0]
              : tippyInstances;
          },
          onUpdate: (props) => {
            component.updateProps(props);
            if (tippyInstance) {
              tippyInstance.setProps({
                getReferenceClientRect: props.clientRect,
              });
            }
          },
          onKeyDown: (props) => {
            if (props.event.key === "Escape") {
              if (tippyInstance) {
                tippyInstance.hide();
              }
              return true;
            }
            return component.ref?.onKeyDown(props);
          },
          onExit: () => {
            if (tippyInstance && typeof tippyInstance.destroy === "function") {
              tippyInstance.destroy();
              tippyInstance = null;
            }
            if (component) {
              component.destroy();
            }
          },
        };
      },
    });

    const editor = new Editor({
      content: props.modelValue || "",
      editable: true,
      shouldRerenderOnTransaction: true, // TipTap 3.x requirement
      extensions: [
        StarterKit.configure({
          blockquote: {
            HTMLAttributes: { class: "tiptap_expander" },
          },
        }),
        // User mentions with @
        Mention.extend({
          name: "userMention",
          addAttributes() {
            return {
              ...this.parent?.(),
              "data-mention-id": {
                default: null,
                parseHTML: (element) => element.getAttribute("data-mention-id"),
                renderHTML: (attributes) => ({
                  "data-mention-id": attributes.id,
                }),
              },
              "data-mention-type": {
                default: "User",
                parseHTML: (element) =>
                  element.getAttribute("data-mention-type"),
                renderHTML: () => ({ "data-mention-type": "User" }),
              },
            };
          },
        }).configure({
          HTMLAttributes: { class: "mention" },
          suggestion: createMentionSuggestion("@", users),
        }),
        // Contact mentions with #
        Mention.extend({
          name: "contactMention",
          addAttributes() {
            return {
              ...this.parent?.(),
              "data-mention-id": {
                default: null,
                parseHTML: (element) => element.getAttribute("data-mention-id"),
                renderHTML: (attributes) => ({
                  "data-mention-id": attributes.id,
                }),
              },
              "data-mention-type": {
                default: "Contact",
                parseHTML: (element) =>
                  element.getAttribute("data-mention-type"),
                renderHTML: () => ({ "data-mention-type": "Contact" }),
              },
            };
          },
        }).configure({
          HTMLAttributes: { class: "mention" },
          suggestion: createMentionSuggestion("#", contacts),
        }),
      ],
      onUpdate: ({ editor }) => {
        emit("update:modelValue", editor.getHTML());
      },
      onFocus: () => {
        // Position cursor at the end when the editor is focused
        setTimeout(() => {
          editor.commands.focus("end");
        }, 0);
      },
    });

    // Watch for modelValue changes to update editor content
    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue !== editor.getHTML()) {
          editor.commands.setContent(newValue || "");

          // Move cursor to end after content update
          setTimeout(() => {
            editor.commands.focus("end");
          }, 0);
        }
      },
    );

    return {
      editor,
      toggle_multiple,
      users,
      contacts,
    };
  },
  methods: {
    applyTemplate(templateHtml) {
      if (!templateHtml) return;
      this.editor.commands.setContent(templateHtml);
    },
    addText(text) {
      this.editor.commands.insertContent(text);
    },
    // Plain text with mentions as [[m:N]] placeholders, for the AI skills.
    getAiDraft() {
      return docToPlaceholderText(this.editor.getJSON());
    },
    // Replaces the content with an AI rewrite (TipTap JSON). Runs as one editor
    // transaction, so Ctrl+Z restores the original text.
    applyAiRewrite(doc) {
      this.editor.chain().focus().setContent(doc, { emitUpdate: true }).run();
    },
    // Replaces the first occurrence of `phrase` in the text with a mention of a
    // colleague ("User") or contact ("Contact"), as one undoable transaction.
    // Returns false when the phrase is no longer in the text.
    replaceTextWithMention(phrase, { type, id, label }) {
      const textNodes = [];
      this.editor.state.doc.descendants((node, pos) => {
        if (node.isText) textNodes.push({ text: node.text, pos });
      });
      const range = findPhraseRange(textNodes, phrase);
      if (!range) return false;

      return this.editor
        .chain()
        .focus()
        .insertContentAt(range, {
          type: type === "Contact" ? "contactMention" : "userMention",
          attrs: { id, label },
        })
        .run();
    },
    insertUserMention() {
      // Get the text before cursor to check if we need a space
      const { from } = this.editor.state.selection;
      const textBefore = this.editor.state.doc.textBetween(
        Math.max(0, from - 1),
        from,
      );

      // If there's a character before cursor (not space/newline), add space first
      if (textBefore && textBefore !== " " && textBefore !== "\n") {
        this.editor.chain().focus().insertContent(" @").run();
      } else {
        this.editor.chain().focus().insertContent("@").run();
      }
    },
    insertContactMention() {
      // Get the text before cursor to check if we need a space
      const { from } = this.editor.state.selection;
      const textBefore = this.editor.state.doc.textBetween(
        Math.max(0, from - 1),
        from,
      );

      // If there's a character before cursor (not space/newline), add space first
      if (textBefore && textBefore !== " " && textBefore !== "\n") {
        this.editor.chain().focus().insertContent(" #").run();
      } else {
        this.editor.chain().focus().insertContent("#").run();
      }
    },
    insertBlockquote() {
      const { from, to } = this.editor.state.selection;
      const hasSelection = from !== to;

      // Check if we're already in a blockquote
      if (this.editor.isActive("blockquote")) {
        // If inside blockquote, exit it by inserting a new paragraph after the blockquote
        // This keeps the content in the blockquote and moves cursor outside
        const { $from } = this.editor.state.selection;

        // Find the blockquote node
        let blockquoteDepth = null;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === "blockquote") {
            blockquoteDepth = d;
            break;
          }
        }

        if (blockquoteDepth !== null) {
          // Get position after the blockquote
          const blockquoteNode = $from.node(blockquoteDepth);
          const blockquotePos = $from.before(blockquoteDepth);
          const afterBlockquote = blockquotePos + blockquoteNode.nodeSize;

          // Insert a new paragraph after the blockquote and move cursor there
          this.editor
            .chain()
            .focus()
            .insertContentAt(afterBlockquote, { type: "paragraph" })
            .setTextSelection(afterBlockquote + 1)
            .run();
        }
      } else if (hasSelection) {
        // If there's a text selection, wrap it in a blockquote
        this.editor.chain().focus().toggleBlockquote().run();
      } else {
        // If not in blockquote and no selection, insert a new blockquote
        const { $to } = this.editor.state.selection;

        // Insert blockquote with paragraph at end of current node
        this.editor
          .chain()
          .focus()
          .insertContentAt($to.after(), {
            type: "blockquote",
            content: [{ type: "paragraph" }],
          })
          .run();

        // Move cursor into the blockquote
        setTimeout(() => {
          const pos = $to.after() + 2; // +2 to get inside the paragraph inside blockquote
          this.editor.commands.setTextSelection(pos);
          this.editor.commands.focus();
        }, 10);
      }
    },
  },
};
</script>

<style lang="scss">
.editor {
  position: relative;

  .editor-wrapper {
    border: 1px solid rgba(0, 0, 0, 0.16);
    border-radius: 4px;
    padding: 12px;
    min-height: 100px;
    background-color: white;

    &:hover {
      border-color: rgba(0, 0, 0, 0.6);
    }

    &:focus-within {
      border-color: #1976d2;
      border-width: 2px;
      padding: 11px;
    }
  }
}

.toolbar-compact {
  gap: 2px;
  .v-btn {
    margin: 0 2px;
  }
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
}

.toolbar-separator {
  width: 1px;
  height: 24px;
  background-color: rgba(0, 0, 0, 0.12);
  margin: 0 6px;
  align-self: center;
}

.ProseMirror {
  outline: none;
  white-space: pre-wrap;
}

.editor__content {
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    text-align: left;
    white-space: pre-wrap;
  }

  p {
    margin: 0.75em 0;
    line-height: 1.5;
  }

  /* Blockquote styling to match global display */
  blockquote {
    background-color: #f8f9fa;
    border-left: 4px solid #90caf9;
    border-radius: 4px;
    padding: 1em 1.2em;
    margin: 1em 0;
    font-size: 0.95em;
    color: #424242;
    line-height: 1.6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

    p {
      margin: 0.5em 0;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  /* Mention styles are defined globally in assets/styles/styles.css */
}

.bubble-menu {
  display: flex;
  background-color: #f5f7fa;
  padding: 0.375rem;
  border-radius: 8px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 1px 2px rgba(0, 0, 0, 0.12);
  margin-bottom: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.05);

  .v-btn {
    &.is-active {
      background-color: rgba(25, 118, 210, 0.1);
      color: #1976d2;
    }
  }
}

.suggestion-list {
  background: white;
  color: #1e1e1e;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  max-height: 220px;
  padding: 0.5rem;

  &__item {
    padding: 0.4rem 0.6rem;
    margin-bottom: 0.2rem;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover,
    &.is-selected {
      background-color: rgba(25, 118, 210, 0.1);
    }
  }
}

.tippy-box {
  background-color: transparent !important;
  border: none !important;
}
</style>
